/**
 * Cloudinary delivery helpers for DWS.
 * Never serve raw originals for UI: always prefer sized/transformed URLs.
 */

/** Default display transforms: auto format/quality, max width, no upscale. */
export const DEFAULT_DELIVERY_TRANSFORMS = 'f_auto,q_auto,c_limit,w_1200';

/** Compact card / thumbnail (experiences grid, product tiles). */
export const CARD_COVER_TRANSFORMS = 'f_auto,q_auto,c_fill,w_400,h_540';

/** Product / POS tile thumbs (square-ish cards). */
export const PRODUCT_THUMB_TRANSFORMS = 'f_auto,q_auto,c_fill,w_400,h_400';

const CLOUDINARY_HOST = 'res.cloudinary.com';

export function hasCloudinaryTransforms(url: string): boolean {
  if (!url) {
    return false;
  }
  return /\/upload\/(?!v\d+\/)[^/]+\/(v\d+\/)?/.test(url);
}

/**
 * Inject delivery transforms on a Cloudinary URL if missing.
 * Non-Cloudinary / data / blob URLs are returned unchanged.
 */
export function ensureTransformedCloudinaryUrl(
  url: string | null | undefined,
  transforms: string = DEFAULT_DELIVERY_TRANSFORMS
): string {
  if (!url || url.indexOf(CLOUDINARY_HOST) === -1) {
    return url || '';
  }
  if (hasCloudinaryTransforms(url)) {
    return url;
  }
  return url.replace(/\/upload\/(v\d+\/)?/, '/upload/' + transforms + '/$1');
}

/**
 * Product file link (often raw secure_url) → optimized delivery URL.
 */
export function productCoverUrl(
  link: string | null | undefined,
  fallback: string = 'assets/images/empty-image.svg',
  transforms: string = PRODUCT_THUMB_TRANSFORMS
): string {
  if (!link) {
    return fallback;
  }
  const normalized = link.replace(/^http:\/\//i, 'https://');
  if (normalized.indexOf(CLOUDINARY_HOST) === -1) {
    return normalized;
  }
  return ensureTransformedCloudinaryUrl(normalized, transforms) || fallback;
}

/**
 * Pick first available size key (never `original`), then ensure transforms.
 */
export function pickTransformedPhotoUrl(
  urls: {
    original?: string;
    s?: string;
    m?: string;
    v?: string;
    l?: string;
    xl?: string;
    vv?: string;
    p?: string;
  } | null | undefined,
  preferredSizes: string[] = ['vv', 'v', 'xl', 'l', 'm', 's'],
  transforms: string = DEFAULT_DELIVERY_TRANSFORMS
): string | null {
  if (!urls) {
    return null;
  }
  const sized = urls as { [key: string]: string | undefined };
  for (let i = 0; i < preferredSizes.length; i++) {
    const candidate = sized[preferredSizes[i]];
    if (candidate) {
      return ensureTransformedCloudinaryUrl(candidate, transforms);
    }
  }
  return null;
}
